import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';
import { fetchWithTimeout } from '@ticom/web-component-utils';
import { ITrainingSeries } from './training-series.interface';

@Component({
    tag: 'ti-training-series',
    styleUrl: 'training-series.scss',
    shadow: true
})

export class TrainingSeries {

    private _io: IntersectionObserver;
    private readonly _statusType = {
        LOADING: 'LOADING',
        READY: 'READY',
        EMPTY: 'EMPTY',
        ERROR: 'ERROR'
    }

    @Element() hostElement: HTMLTiTrainingSeriesElement;

    /**
     * State entry for keeping track of our item data
     */
    @State() items: Array<ITrainingSeries> = [];
    @Watch('items')
    handleItemsChange() {
        this.tiItemsUpdated.emit({
            items: this.items
        });
    }

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @default _statusType.Loading
     */
    @State() status: string = this._statusType.LOADING;

    /**
     * Api URL - where we get the data from
     * https://ti.com/bin/ti/folder/video-series
     *
     * @type {string}
     */
    @Prop() apiUrl: string = '/bin/ti/folder/video-series';
    @Watch('apiUrl')
    handleApiUrlChange(newValue: string, oldValue: string) {
        this._handleKeyPropUpdates(newValue, oldValue);
    }

    /**
     * If the series doesn't have a thumbnail, this
     * graphic will be used.
     */
    @Prop() defaultThumbnail: string = 'https://www.ti.com/content/dam/ticom/images/training/video-playlist-thumbnail.jpg';

    /**
     * The type of folder we are using this component in.
     * Will be passed to the web service to pull relevant content.
     *
     * @type {string} One of "product_tree" or "tools_software_tree" or "applications_designs_tree"
     * @default "product_tree"
     */
    @Prop() folderType: 'product_tree' | 'tools_software_tree' | 'applications_designs_tree' = 'product_tree';

    /**
     * GPN
     * @type {string}
     */
    @Prop() gpn: string;
    @Watch('gpn')
    handleGpnChange(newValue: string, oldValue: string) {
        this._handleKeyPropUpdates(newValue, oldValue);
    }

    /**
     * This is the label for the "(n videos)".
     * Brackets and number will be provided in render.
     */
    @Prop() labelNumVideos: string = 'videos';

    /**
     * if lazy loaded or not
     */
    @Prop() lazy: boolean = false;

    /**
     * Locale
     * Can be 'en-US', 'ja-JP','zh-CN', 'ko-KR', 'de-DE', 'zh-TW'
     *
     * @type {string}
     * @default "en-US"
     */
    @Prop({ mutable: true }) locale: string = 'en-US';

    /**
     * Number of entries to display.
     * Maximum 3 based on business requirements
     * https://confluence.itg.ti.com/download/attachments/264866902/OTP_Support_Transition_v1.1.doc?version=1&modificationDate=1571840857000&api=v2
     *
     * @type {number}
     * @default 3
     */
    @Prop() numToDisplay: number = 3;

    /**
     * Optionally hide "view all..." link in component by setting
     * this to false.
     *
     * The link will be populated by the tool folder, in
     * which case this should be set to false, and the link
     * will appear through a slot.
     *
     * @type {boolean}
     * @default false
     */
    @Prop() showViewAllLink: boolean = false;

    /**
     * Text used for the "view all..." link
     * @type {string}
     * @default "View all videos"
     */
    @Prop({ mutable: true }) viewAllLabel: string = 'View all videos';

    /**
     * Url used for the "view all..." link
     * @type {string}
     *
     * @default "/sitesearch/en-us/docs/universalsearch.tsp?langPref=en-US&searchTerm=video#f:videos=[Video]"
     */
    @Prop({ mutable: true }) viewAllUrl: string = '/sitesearch/en-us/docs/universalsearch.tsp?langPref=en-US&searchTerm=video#f:videos=[Video]';

    /**
     * Event for when there's a problem getting the data
     */
    @Event() tiDataError: EventEmitter;

    /**
     * Event for when items are updated
     */
    @Event() tiItemsUpdated: EventEmitter;

    /**
     * Lifecycle event handler
     */
    componentWillLoad() {
        if (!this.gpn && !this.hostElement.querySelector('script[type=\'application/json\']')) {
            this.status = this._statusType.ERROR;
            return;
        }

        if (!this.lazy) {
            this._updateData();
        }
    }

    componentDidLoad() {
        this._addIntersectionObserver();
    }

    disconnectedCallback() {
        this._removeIntersectionObserver();
    }

    private _addIntersectionObserver() {
        if (this.lazy && 'IntersectionObserver' in window) {
            this._io = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    this._updateData();
                    this._removeIntersectionObserver();
                }
            });
            if (this._io['USE_MUTATION_OBSERVER'] != null) {
                this._io['USE_MUTATION_OBSERVER'] = false;
            }
            this._io.observe(this.hostElement);
        }
    }

    private _handleKeyPropUpdates(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue.length > 0) {
            this.status = this._statusType.LOADING;
        }
        this._updateData();
    }

    private _removeIntersectionObserver() {
        if (this._io) {
            this._io.disconnect();
            this._io = null;
        }
    }

    private async _updateData() {
        // Jack API documentation can be found here:
        // https://confluence.itg.ti.com/pages/viewpage.action?pageId=193677339
        let trainingResponse;
        let training;

        // First check if the data was pre-loaded
        const trainingEl: HTMLElement = this.hostElement.querySelector<HTMLScriptElement>('script[type=\'application/json\']');
        if (trainingEl?.textContent?.trim()) {
            training = JSON.parse(trainingEl.textContent.trim());
            if ('error' in training) {
                this.status = this._statusType.ERROR;
            }
        } else {
            // Continue normal fetching of data
            // fetch training-series based on the locale
            // default to 'EN' for en-US
            const languages = ['en-us'];
            // not en-US
            if (this.locale !== 'en-US') {
                languages.unshift(this.locale);
            }
            const lang = languages.join(',').toLowerCase();


            try {
                trainingResponse = await fetchWithTimeout(`${this.apiUrl}?fm=${this.gpn}&result=${this.numToDisplay}&foldertype=${this.folderType}&language=${lang}`);
                if (!trainingResponse) {
                    this.status = this._statusType.ERROR;
                    this.tiDataError.emit({
                        error: "Couldn't get trainingResponse"
                    });
                    return;
                }
                // if the item isn't found, the web service is actually set
                // up to return a 200, but with a "error" property in the
                // json response. This helps avoid false alarms with monitoring,
                // as there are probably a number of parts that don't have data
                // but that doesn't mean we have a real problem.
                if (trainingResponse.error) {
                    throw new Error(`Request for ${this.gpn} returned 404`);
                }
                // if we're hooking up to regular json, there will be an ok
                // if not, we'll try to use training response as is
                training = (trainingResponse.ok)
                    ? await trainingResponse.json()
                    : trainingResponse;
            } catch (e) {
                this.status = this._statusType.ERROR;
                this.tiDataError.emit({
                    error: "Couldn't get trainingResponse"
                });
                return;
            }
        }

        if (!training) {
            this.status = this._statusType.ERROR;
            this.tiDataError.emit({
                error: "Couldn't get trainingResponse"
            });
            return;
        }
        if (!training.courses || !training.courses.length || training.courses.length === 0) {
            // no training courses
            this.status = this._statusType.EMPTY;
            return;
        }
        // do some processing on the data so that we don't clog up the render()
        this.items = training.courses.map(course => {
            // sub in the thumbnail if a course is missing it's thumbnail
            if (!course.thumbnail) course.thumbnail = this.defaultThumbnail;

            // set up the number of videos text
            course.numVideos = (course.series && course.series.number_of_courses && course.series.number_of_courses > 1)
                ? ' (' + this.labelNumVideos.replace('{0}', course.series.number_of_courses.toString()) + ')'
                : '';

            // insert the elipses if we're close to 160 chars and don't end in a period
            if (course.short_description.length > 155 && course.short_description.substring(course.short_description.length - 1, 1) !== '.') {
                course.short_description += '&mldr;';
            }

            return course;
        });

        if (this.items.length > 0) {
            this.status = this._statusType.READY;
        } else {
            this.status = this._statusType.ERROR;
        }
    }

    render() {
        if (this.status === this._statusType.LOADING) {
            return (<Host>
                <div class="ti-training-series-progress-container">
                    <ti-progress></ti-progress>
                </div>
            </Host>);
        } else if (this.status === this._statusType.READY) {
            const numToDisplay = Math.min(this.numToDisplay, this.items.length);
            return (
                <Host>
                    <div class="ti-training-series-container">
                        <slot name="title" />
                        {
                            <ti-carousel>
                                {
                                    this.items.map((item, index) =>
                                        index < numToDisplay
                                            ?
                                            <ti-carousel-item>
                                                <article>
                                                    <a href={item.url}>
                                                        <img src={item.thumbnail} alt={item.title} />
                                                    </a>

                                                    <a slot="title" href={item.url}>
                                                        <span innerHTML={item.title}></span>
                                                        <span>{item.numVideos}</span>
                                                    </a>

                                                    <p slot="description" innerHTML={item.short_description}></p>
                                                </article>
                                            </ti-carousel-item>
                                            :
                                            null
                                    )
                                }
                            </ti-carousel>
                        }
                        {this.showViewAllLink
                            ? <a href={this.viewAllUrl}>{this.viewAllLabel}</a>
                            : <slot name="view-all" />
                        }
                    </div>
                </Host>
            );
        } else if (this.status === this._statusType.EMPTY) {
            this.hostElement.innerHTML = '<!-- No training materials available -->';
            this.hostElement.style.display = 'none';
            return null;
        } else {
            this.hostElement.outerHTML = '<!-- ti-training-series load error -->';
            this.hostElement.style.display = 'none';
            return null;
        }
    }
}
